/**
 * 영화관 스타일 좌석 선택 메인 컴포넌트
 * 구역 선택 → 좌석 선택 → 예약/입실 플로우 관리
 */
import React, { useState, useCallback } from 'react';
import { Zone, SeatInZone } from '../../../services/zoneApi';
import ZoneSelection from '../../../components/ZoneSelection';
import SeatMap from '../../../components/SeatMap';

type SelectionStep = 'zone' | 'seat' | 'confirmation';

interface CinemaStyleSeatSelectionProps {
  onComplete?: (
    zone: Zone,
    seat: SeatInZone,
    action: 'checkin' | 'reserve'
  ) => void;
  onCancel?: () => void;
}

const CinemaStyleSeatSelection: React.FC<CinemaStyleSeatSelectionProps> = ({
  onComplete,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState<SelectionStep>('zone');
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [selectedSeat, setSelectedSeat] = useState<SeatInZone | null>(null);

  // 구역 선택 핸들러
  const handleZoneSelect = useCallback((zone: Zone) => {
    setSelectedZone(zone);
    setCurrentStep('seat');
  }, []);

  // 좌석 선택 핸들러
  const handleSeatSelect = useCallback((seat: SeatInZone | null) => {
    setSelectedSeat(seat);
  }, []);

  // 구역 선택으로 돌아가기
  const handleBackToZones = useCallback(() => {
    setSelectedZone(null);
    setSelectedSeat(null);
    setCurrentStep('zone');
  }, []);

  // 입실하기 핸들러
  const handleCheckIn = useCallback(
    (seat: SeatInZone) => {
      if (selectedZone && onComplete) {
        onComplete(selectedZone, seat, 'checkin');
      }
    },
    [selectedZone, onComplete]
  );

  // 예약하기 핸들러
  const handleReserve = useCallback(
    (seat: SeatInZone) => {
      if (selectedZone && onComplete) {
        onComplete(selectedZone, seat, 'reserve');
      }
    },
    [selectedZone, onComplete]
  );

  return (
    <div className="min-h-screen">
      {currentStep === 'zone' && (
        <ZoneSelection onZoneSelect={handleZoneSelect} onBack={onCancel} />
      )}

      {currentStep === 'seat' && selectedZone && (
        <SeatMap
          zone={selectedZone}
          onBack={handleBackToZones}
          onSeatSelect={handleSeatSelect}
          selectedSeat={selectedSeat}
          onCheckIn={handleCheckIn}
          onReserve={handleReserve}
        />
      )}
    </div>
  );
};

export default CinemaStyleSeatSelection;
